// One landing page per course — the destination for that course's ads.
//
// These are conversion pages, not another section of the site: one course, one
// promise, one form. The interactive demo carries the argument, and everything
// below it exists to answer the questions a parent has between "that looks fun"
// and leaving a phone number.
//
// The page deliberately does not depend on the courses 3D world: an ad click
// lands here cold, often on a phone, and must not wait on an HDR probe and four
// islands to render something.
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, BookOpen, CheckCircle, ChevronLeft, Clock, Download,
  MessageCircle, Phone, Send, Smartphone, Sparkles, User, Users, Wallet,
} from 'lucide-react';
import emailjs from '@emailjs/browser';
import LANDING_CONTENT from '../components/courses/landingContent';
import { COURSES } from '../components/courses/courseData';
import { injectFonts, T } from '../utils/fonts';
import { track, trackCustom, trackLead, leadReference } from '../utils/pixel';
import useReducedMotion from '../utils/useReducedMotion';

/** The same EmailJS destination the contact page uses, so leads from the ads
    arrive in the existing inbox rather than a second one nobody watches. */
const EMAILJS = {
  serviceID: 'service_j25e6gl',
  templateID: 'template_1hn9h46',
  publicKey: 'X4BurVzKo6HJ-wI3-',
};

/* ── Page metadata ──
   An ad link gets shared, pasted into WhatsApp and previewed, so the tags
   matter more here than on a page people reach from the nav. Restored on
   unmount — this is an SPA and the next route would otherwise inherit them. */
const useLandingMeta = (title, description) => {
  useEffect(() => {
    const previous = { title: document.title, tags: [] };
    document.title = title;

    const set = (selector, value) => {
      let tag = document.head.querySelector(selector);
      let created = false;
      if (!tag) {
        tag = document.createElement('meta');
        const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/);
        tag.setAttribute(key, name);
        document.head.appendChild(tag);
        created = true;
      }
      previous.tags.push({ tag, created, value: tag.getAttribute('content') });
      tag.setAttribute('content', value);
    };

    set('meta[name="description"]', description);
    set('meta[property="og:title"]', title);
    set('meta[property="og:description"]', description);
    set('meta[property="og:type"]', 'website');

    return () => {
      document.title = previous.title;
      previous.tags.forEach(({ tag, created, value }) => {
        if (created) tag.remove();
        else if (value !== null) tag.setAttribute('content', value);
      });
    };
  }, [title, description]);
};

/* ── Lead form ────────────────────────────────────────────────────────────── */

const LeadForm = ({ course, source }) => {
  const formRef = useRef();
  const [status, setStatus] = useState('idle');
  // One reference per mounted form, so the value sent to Meta is the same one
  // written into the enquiry email.
  const reference = useMemo(() => leadReference(), []);

  const submit = (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');

    const form = formRef.current;
    const data = new FormData(form);
    const phone = data.get('user_phone');
    const name = String(data.get('user_name') || '').trim();

    emailjs.sendForm(EMAILJS.serviceID, EMAILJS.templateID, form, EMAILJS.publicKey).then(
      () => {
        setStatus('success');
        form.reset();
        // Meta's standard conversion event — this is what the campaigns
        // optimise toward, so it fires only on a confirmed send.
        //
        // Consent for this is given by submitting: the notice under the button
        // says what happens and links the privacy policy, which is the pattern
        // lead forms generally use. Keep the two in step — if the notice ever
        // stops mentioning the transfer, this call has nothing standing behind
        // it.
        trackLead({
          phone,
          firstName: name.split(' ')[0],
          reference,
          contentName: course.title,
          contentCategory: course.id,
          leadSource: source,
        });
      },
      (error) => {
        console.error(error?.text || error);
        setStatus('error');
      }
    );
  };

  if (status === 'success') {
    return (
      <div className="lp-sent" role="status">
        <CheckCircle size={40} aria-hidden="true" />
        <h3>קיבלנו את הפרטים</h3>
        <p>נחזור אליכם בשיחה קצרה בהקדם. אם זה דחוף — אפשר גם בוואטסאפ.</p>
        <a className="gg-btn" href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer">
          <MessageCircle size={18} aria-hidden="true" /> לוואטסאפ
        </a>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={submit} className="lp-form">
      {/* Read by the existing EmailJS template; keeps ad leads identifiable. */}
      <input type="hidden" name="course_interest" value={`${course.title} (${source})`} readOnly />
      <input type="hidden" name="lead_source" value={source} readOnly />
      {/* Travels with the enquiry email so this lead can be found again later. */}
      <input type="hidden" name="lead_reference" value={reference} readOnly />

      <div className="lp-field">
        <label htmlFor="lp-name">שם ההורה או האפוטרופוס</label>
        <div className="lp-input-wrap">
          <User size={18} aria-hidden="true" />
          <input
            id="lp-name" name="user_name" type="text" required autoComplete="name"
            className="gg-input" placeholder="השם שלכם" disabled={status === 'loading'}
          />
        </div>
      </div>

      <div className="lp-field">
        <label htmlFor="lp-phone">טלפון של ההורה או האפוטרופוס</label>
        <div className="lp-input-wrap">
          <Smartphone size={18} aria-hidden="true" />
          <input
            id="lp-phone" name="user_phone" type="tel" required autoComplete="tel" dir="ltr"
            className="gg-input" placeholder="050-0000000" disabled={status === 'loading'}
          />
        </div>
      </div>

      <div className="lp-field">
        <label htmlFor="lp-age">גיל התלמיד</label>
        <div className="lp-input-wrap">
          <Users size={18} aria-hidden="true" />
          <select
            id="lp-age" name="student_age" required defaultValue=""
            className="gg-input" disabled={status === 'loading'}
          >
            <option value="" disabled>בחרו קבוצת גיל</option>
            <option value="10-13">10-13</option>
            <option value="14-18">14-18</option>
            <option value="אחר">אחר</option>
          </select>
        </div>
      </div>

      <button type="submit" className="gg-btn lp-submit" disabled={status === 'loading'}>
        {status === 'loading'
          ? 'שולח...'
          : <><Send size={18} aria-hidden="true" /> לתיאום שיעור ניסיון חינם</>}
      </button>

      {status === 'error' && (
        <p className="lp-error" role="alert">
          <AlertCircle size={15} aria-hidden="true" /> אירעה שגיאה בשליחה. נסו שוב, או דברו איתנו בוואטסאפ.
        </p>
      )}

      <p className="lp-consent">
        בשליחת הטופס אני מאשר/ת שהפרטים ישמשו ליצירת קשר בנוגע לקורס וכן
        להעברה מוצפנת ל‑Meta למדידת ביצועי המודעות, בהתאם
        ל<Link to="/privacy" className="gg-link">מדיניות הפרטיות</Link>.
      </p>
    </form>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */

const CourseLanding = ({ courseId }) => {
  const course = COURSES.find((c) => c.id === courseId);
  const content = LANDING_CONTENT[courseId];
  const reduced = useReducedMotion();
  const demoFired = useRef(false);

  const title = `${course.title} לילדים ונוער | GoGameDev3D`;
  const description = `${content.lead} גילאי 10-18, ${course.totalHours}.`;
  useLandingMeta(title, description);

  useEffect(() => {
    injectFonts();
    track('ViewContent', { content_name: course.title, content_category: course.id });
  }, [course]);

  // Engagement with the demo is the strongest on-page signal these campaigns
  // have; reported once each so a determined clicker can't skew the numbers.
  const onFirstUse = useCallback(() => {
    if (demoFired.current) return;
    demoFired.current = true;
    trackCustom('DemoUse', { course: courseId });
  }, [courseId]);
  const onRun = useCallback(() => trackCustom('DemoRun', { course: courseId }), [courseId]);

  const { Demo } = content;
  const source = `דף נחיתה — קמפיין ${course.hud}`;

  const facts = [
    { icon: Wallet, label: 'מחיר לחודש', value: `₪${course.price}` },
    { icon: Users, label: 'גילאים', value: '10-13 / 14-18' },
    { icon: Clock, label: 'היקף הקורס', value: course.totalHours },
    { icon: BookOpen, label: 'מתכונת', value: course.schedule },
  ];

  return (
    <div
      className="lp gg-bg"
      dir="rtl"
      style={{ '--lp-accent': course.accent, '--lp-accent-ink': course.accentInk }}
    >
      {/* ── Hero ── */}
      <header className="lp-hero">
        <p className="gg-node">{course.hud} · גילאי 10-18</p>
        <h1>
          {content.headline[0]}<br />
          <span className="lp-accent">{content.headline[1]}</span>
        </h1>
        <p className="lp-lead">{content.lead}</p>

        <div className="lp-cta">
          {/* The ads' button says "שיעור ניסיון חינם", so the page they land on
              has to open with the same offer — a click that arrives on a
              different promise is the most expensive kind of bounce. */}
          <a href="#lead" className="gg-btn">
            <Sparkles size={18} aria-hidden="true" /> לשיעור ניסיון חינם
          </a>
          <a href={course.syllabusLink} className="gg-btn-ghost" target="_blank" rel="noopener noreferrer">
            <Download size={16} aria-hidden="true" /> הורדת הסילבוס המלא
          </a>
        </div>

        <ul className="lp-badges">
          {content.badges.map((badge) => <li key={badge}>{badge}</li>)}
          <li>{course.totalHours}</li>
        </ul>
      </header>

      {/* ── The demo: the whole pitch, playable ── */}
      <section className="lp-section" aria-labelledby="demo-title">
        <h2 id="demo-title">{content.demoTitle}</h2>
        <p className="lp-sub">{content.demoSub}</p>
        <Demo
          accent={course.accent}
          accentInk={course.accentInk}
          reduced={reduced}
          onFirstUse={onFirstUse}
          onRun={onRun}
        />
      </section>

      {/* ── Facts ── */}
      <section className="lp-facts" aria-label="פרטי הקורס">
        {facts.map(({ icon: Icon, label, value }) => (
          <div key={label} className="lp-fact gg-card">
            <Icon size={20} aria-hidden="true" />
            <p className="lp-fact-label">{label}</p>
            <p className="lp-fact-value">{value}</p>
          </div>
        ))}
      </section>

      {/* ── Syllabus ── */}
      <section className="lp-section" aria-labelledby="syllabus-title">
        <h2 id="syllabus-title">{content.syllabusTitle} ב-{course.totalHours}</h2>
        <p className="lp-sub">{content.syllabusSub}</p>
        <ol className="lp-syllabus">
          {content.syllabus.map((topic, i) => (
            <li key={topic}><span className="lp-num">{i + 1}</span>{topic}</li>
          ))}
        </ol>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section" aria-labelledby="faq-title">
        <h2 id="faq-title">שאלות שהורים שואלים</h2>
        <div className="lp-faq">
          {content.faq.map(({ q, a }) => (
            <details key={q} className="gg-card">
              <summary>{q}<ChevronLeft size={18} aria-hidden="true" /></summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Lead ── */}
      <section className="lp-section lp-lead-section" id="lead" aria-labelledby="lead-title">
        <div className="lp-lead-grid">
          <div>
            <h2 id="lead-title">קודם כל — שיעור ניסיון חינם</h2>
            <p className="lp-sub">
              השאירו שם וטלפון ונחזור אליכם: נתאם שיעור ניסיון, נתאים קבוצה לפי גיל ורמה,
              ונענה על מה שנשאר פתוח. בלי התחייבות.
            </p>
            <div className="lp-direct">
              <a href="tel:0549447314" className="lp-direct-row">
                <Phone size={18} aria-hidden="true" /><span dir="ltr">054-944-7314</span>
              </a>
              <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer" className="lp-direct-row">
                <MessageCircle size={18} aria-hidden="true" /><span>וואטסאפ</span>
              </a>
            </div>
          </div>
          <div className="gg-card lp-form-card"><LeadForm course={course} source={source} /></div>
        </div>
      </section>

      {/* Mobile-only sticky action bar — on a phone the form is a long scroll
          away for most of the page, and this is where ad traffic lands. */}
      <div className="lp-sticky">
        <a href="#lead" className="gg-btn">שיעור ניסיון חינם</a>
        <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer"
           className="gg-btn-ghost" aria-label="פנייה בוואטסאפ">
          <MessageCircle size={20} aria-hidden="true" />
        </a>
      </div>

      <style>{`
        .lp {
          min-height: 100vh;
          padding: clamp(90px, 15vw, 130px) clamp(16px, 5vw, 40px) clamp(120px, 18vw, 90px);
          font-family: ${T.fontBody};
          color: ${T.text};
          /* Read by the demo shell, so each course's stage glows in its own
             accent without every demo restating the palette. */
          --demo-accent: var(--lp-accent);
          --demo-glow: color-mix(in srgb, var(--lp-accent) 12%, transparent);
          --demo-glow-light: color-mix(in srgb, var(--lp-accent) 20%, transparent);
          --demo-code-wash: color-mix(in srgb, var(--lp-accent) 16%, transparent);
        }
        .lp > * { max-width: 1080px; margin-inline: auto; }
        .lp h1, .lp h2 { font-family: ${T.fontDisplay}; font-weight: 400; }
        .lp-accent { color: var(--lp-accent); }
        :root[data-theme="light"] .lp-accent { color: var(--lp-accent-ink); }

        .lp-hero { text-align: center; padding-bottom: clamp(36px, 7vw, 64px); }
        .lp-hero .gg-node { margin-bottom: 18px; }
        .lp-hero h1 {
          font-size: clamp(1.9rem, 6.4vw, 3.4rem);
          margin: 0 0 18px; line-height: 1.25;
        }
        .lp-lead {
          font-size: clamp(1rem, 2.8vw, 1.18rem);
          color: ${T.muted}; margin: 0 auto 30px; max-inline-size: 60ch;
        }
        .lp-cta { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
        .lp-badges {
          list-style: none; margin: 26px 0 0; padding: 0;
          display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;
        }
        .lp-badges li {
          font-size: 0.82rem; font-weight: 700; color: ${T.muted};
          padding: 6px 14px; border-radius: 99px;
          border: 1px solid ${T.line}; background: ${T.panel};
          max-inline-size: none;
        }

        .lp-section { padding: clamp(36px, 7vw, 64px) 0; scroll-margin-top: 90px; }
        .lp-section h2 { font-size: clamp(1.5rem, 4.4vw, 2.2rem); margin: 0 0 12px; }
        .lp-sub { color: ${T.muted}; margin: 0 0 28px; font-size: clamp(0.95rem, 2.5vw, 1.05rem); }

        .lp-facts {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: clamp(10px, 2vw, 16px);
        }
        .lp-fact { padding: 18px 16px; text-align: center; color: var(--lp-accent); }
        :root[data-theme="light"] .lp-fact { color: var(--lp-accent-ink); }
        .lp-fact-label {
          margin: 8px 0 2px; font-size: 0.75rem; font-weight: 700;
          letter-spacing: 0.05em; color: ${T.faint}; max-inline-size: none;
        }
        .lp-fact-value {
          margin: 0; font-size: clamp(0.85rem, 2.4vw, 1.05rem);
          font-weight: 800; color: ${T.text}; max-inline-size: none; line-height: 1.4;
        }

        .lp-syllabus {
          list-style: none; margin: 0; padding: 0;
          display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .lp-syllabus li {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 16px; border-radius: 12px;
          background: ${T.panel}; border: 1px solid ${T.lineSoft};
          font-size: 0.95rem; max-inline-size: none;
        }
        .lp-num {
          flex-shrink: 0; width: 26px; height: 26px; border-radius: 8px;
          display: grid; place-items: center;
          background: color-mix(in srgb, var(--lp-accent) 16%, transparent);
          color: var(--lp-accent);
          font-family: ${T.fontHud}; font-size: 0.72rem;
        }
        :root[data-theme="light"] .lp-num { color: var(--lp-accent-ink); }

        .lp-faq { display: flex; flex-direction: column; gap: 10px; }
        .lp-faq details { padding: 0 18px; }
        .lp-faq summary {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 16px 0; cursor: pointer; font-weight: 700; list-style: none;
          font-size: clamp(0.95rem, 2.6vw, 1.05rem);
        }
        .lp-faq summary::-webkit-details-marker { display: none; }
        .lp-faq summary svg { flex-shrink: 0; color: ${T.godot}; transition: transform .25s ease; }
        .lp-faq details[open] summary svg { transform: rotate(-90deg); }
        .lp-faq summary:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 2px; border-radius: 6px; }
        .lp-faq p { margin: 0 0 18px; color: ${T.muted}; font-size: 0.95rem; }

        .lp-lead-grid {
          display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
          gap: clamp(20px, 4vw, 40px); align-items: start;
        }
        .lp-form-card { padding: clamp(20px, 4vw, 30px); }
        .lp-direct { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
        .lp-direct-row {
          display: inline-flex; align-items: center; gap: 10px;
          color: ${T.text}; text-decoration: none; font-weight: 700;
          padding: 12px 16px; border-radius: 12px;
          background: ${T.panel}; border: 1px solid ${T.line};
          min-height: 48px; box-sizing: border-box;
        }
        .lp-direct-row svg { color: var(--lp-accent); flex-shrink: 0; }
        :root[data-theme="light"] .lp-direct-row svg { color: var(--lp-accent-ink); }
        .lp-direct-row:hover { border-color: ${T.godot}; }
        .lp-direct-row:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 2px; }

        .lp-form { display: flex; flex-direction: column; gap: 16px; }
        .lp-field label {
          display: block; font-size: 0.82rem; font-weight: 700;
          color: ${T.muted}; margin-bottom: 6px;
        }
        .lp-input-wrap { position: relative; display: flex; align-items: center; }
        .lp-input-wrap > svg {
          position: absolute; inset-inline-start: 14px;
          color: ${T.faint}; pointer-events: none;
        }
        .lp-input-wrap:focus-within > svg { color: ${T.godot}; }
        .lp-submit { width: 100%; margin-top: 4px; }

        .lp-error {
          display: flex; align-items: center; gap: 6px; margin: 0;
          color: ${T.red}; font-size: 0.85rem;
        }
        .lp-consent { margin: 0; font-size: 0.78rem; color: ${T.faint}; line-height: 1.6; }
        .lp-sent { text-align: center; padding: 20px 0; color: var(--lp-accent); }
        :root[data-theme="light"] .lp-sent { color: var(--lp-accent-ink); }
        .lp-sent h3 { margin: 12px 0 8px; font-family: ${T.fontDisplay}; font-weight: 400; color: ${T.text}; }
        .lp-sent p { margin: 0 auto 20px; color: ${T.muted}; font-size: 0.95rem; }

        .lp-sticky { display: none; }

        @media (max-width: 820px) {
          .lp-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .lp-syllabus { grid-template-columns: minmax(0, 1fr); }
          .lp-lead-grid { grid-template-columns: minmax(0, 1fr); }
          .lp-sticky {
            position: fixed; inset: auto 0 0 0; z-index: 40;
            display: flex; gap: 10px; align-items: center;
            padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
            background: ${T.navBgScrolled};
            border-top: 1px solid ${T.line};
            backdrop-filter: blur(12px);
          }
          .lp-sticky .gg-btn { flex: 1; }
          .lp-sticky .gg-btn-ghost { width: 52px; padding: 12px 0; }
        }
      `}</style>
    </div>
  );
};

export default CourseLanding;
